import { useState } from 'react';
import Markdown from 'react-markdown';
import { CodeBlock, theme } from '@agentisai/design-system';
import type { GetArtifactData, TurnArtifact } from '@agentisai/shared-types';

/**
 * Map a fetched artifact onto the shape {@link ArtifactPreview} renders, or
 * `null` when it has nothing this component can show (the caller then renders
 * its download-only note).
 *
 * ONE definition of "previewable", used by every panel: the two that existed
 * drifted — the Files rail learned PDFs (Drop-7 S02) and the conversation's
 * deliverables panel did not, so the same PDF previewed in one place and read
 * "no inline preview for this file type" in the other.
 *
 * `url` is `previewUrl` — the inline-disposed presign — NOT `data.url`, which
 * is attachment-disposed for the Download link and forces a download inside an
 * iframe too.
 */
export function previewableArtifact(data: GetArtifactData): TurnArtifact | null {
  // Text-family needs `content`; a PDF renders from `previewUrl` in a native
  // iframe. Anything else has no inline form.
  const canPreview =
    data.content != null || (data.previewUrl != null && data.contentType.includes('pdf'));
  if (!canPreview) return null;
  return {
    filename: data.filename,
    contentType: data.contentType,
    sizeBytes: data.sizeBytes,
    key: data.artifactId,
    url: data.previewUrl,
    preview: data.content,
  };
}

// The agent's ADR-0077 policy stamp, only at the very top (after a leading
// doctype) — the same rule the agent uses to decide a page is policed at all.
const POLICY_STAMP =
  /^\s*(?:<!doctype[^>]*>)?<meta http-equiv="Content-Security-Policy" content="default-src 'none';[^"]*?connect-src ([^;"]*);/i;

/**
 * The live-data hosts an HTML page declared (ADR-0077 §5), read from the policy
 * stamp at its top — the hosts its CSP actually lets it call. `[]` when none
 * (the default) or the page carries no stamp. Shown as "Connects to …".
 */
export function declaredConnectHosts(content: string | undefined): string[] {
  const hosts = content?.match(POLICY_STAMP)?.[1] ?? '';
  return hosts.split(/\s+/).filter((h) => h.startsWith('https://'));
}

/** CodeBlock language stamp for a preview's content type. */
function previewLanguage(contentType: string): string | undefined {
  if (contentType.includes('json')) return 'json';
  return undefined;
}

/**
 * ArtifactPreview — renders an artifact's inline preview by content type
 * (ADR-0040 / Stage 3c):
 *   - HTML → iframe loaded from the inline-disposed presign (`srcdoc` only
 *     as a fallback) so agent-built interactive HTML runs instead of
 *     rendering inert (ADR-0077). Sandbox: scripts, forms (a `<form>`
 *     calculator's submit handler never fires without it) and modals.
 *     `allow-same-origin` is deliberately withheld — the frame keeps an opaque
 *     origin (no cookies/storage/parent-DOM reach). Network egress is capped
 *     by the CSP `<meta>` the agent stamps into every HTML page on write
 *     (`connect-src`/`form-action 'none'`), not by this component;
 *   - markdown → rich render via react-markdown (safe by default — raw HTML
 *     in the source is NOT rendered; the Stage 3b PRD → spec document flow
 *     is the "real report" ADR-0040 deferred this for);
 *   - anything else text-family → DS CodeBlock.
 * Content only — callers own any disclosure/card chrome around it.
 */
export function ArtifactPreview({ artifact }: { artifact: TurnArtifact }) {
  // Every refetch (focus after staleTime, any upload/delete invalidation) brings
  // a freshly-signed URL for the SAME object; feeding it to the iframe reloads
  // the frame and wipes whatever the user was doing in it. Keep the first URL
  // until the artifact or its content actually changes (React's "adjust state
  // during render" pattern — no effect). A loaded frame never re-requests it,
  // so the 1h presign expiry doesn't bite.
  const [frame, setFrame] = useState({
    key: artifact.key,
    preview: artifact.preview,
    url: artifact.url,
  });
  if (frame.key !== artifact.key || frame.preview !== artifact.preview) {
    setFrame({ key: artifact.key, preview: artifact.preview, url: artifact.url });
  }
  // (A render that calls setFrame is discarded and re-run with the new state.)
  const frameUrl = frame.url;

  // PDFs are binary — no text content to inline. Render the presigned GET URL
  // in a native browser PDF viewer. No sandbox: sandboxed iframes block PDF
  // navigation in all major browsers.
  if (artifact.contentType.includes('pdf') && frameUrl) {
    return (
      <iframe
        src={frameUrl}
        title={`Preview of ${artifact.filename}`}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          border: 0,
        }}
      />
    );
  }

  if (!artifact.preview) return null;

  if (artifact.contentType.includes('html')) {
    // Fill the ArtifactWorkspace body (a `flex: 1` panel) so the preview is a
    // full-height viewport of the agent's page — like a browser tab — instead
    // of a fixed 280px slice leaving the column half-empty. `display: block`
    // drops the inline-replaced descender gap that would otherwise force a
    // scrollbar; no border/radius since the panel (tab + meta strips) already
    // frames it, and a full-bleed viewport reads as the document surface. The
    // agent's page scrolls inside the frame when it's taller than the panel.
    // Prefer the inline presign: a `srcdoc` frame inherits the SPA's
    // `script-src 'self'` CSP, which blocks the page's inline <script> — the
    // agent's interactive HTML rendered but never ran. A URL-loaded frame isn't
    // bound by our CSP (the bucket is in `frame-src`). `srcdoc` stays the
    // fallback for an older agent that sends no previewUrl.
    return (
      <iframe
        sandbox="allow-scripts allow-forms allow-modals"
        {...(frameUrl ? { src: frameUrl } : { srcDoc: artifact.preview })}
        title={`Preview of ${artifact.filename}`}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          border: 0,
          background: theme.color.neutral.bgSurface,
        }}
      />
    );
  }

  if (artifact.contentType.includes('markdown')) {
    return (
      <div
        style={{
          padding: `0 ${theme.space[2]}`,
          fontSize: theme.typography.fontSize.bodySm,
          lineHeight: theme.typography.lineHeight.docBody,
          color: theme.color.neutral.body,
          overflowWrap: 'break-word',
        }}
      >
        <Markdown>{artifact.preview}</Markdown>
      </div>
    );
  }

  return <CodeBlock code={artifact.preview} language={previewLanguage(artifact.contentType)} />;
}
ArtifactPreview.displayName = 'ArtifactPreview';
